import { Link, useParams } from 'react-router';
import { type ApiRequestDetail, api } from '../api';
import { CodeBlock, Copyable, ErrorNote, formatDateTime, PageHeader } from '../components/ui';
import { useLoad } from '../hooks';
import { Method, StatusCode } from './Logs';

const pretty = (body: unknown) =>
  body === null || body === undefined ? null : JSON.stringify(body, null, 2);

/** Links an email or domain ID in the path to its own page. */
function relatedLink(path: string, response: unknown) {
  const id =
    path.match(/^\/emails\/(em_\w+)/)?.[1] ??
    (path === '/emails' && response && typeof response === 'object' && 'id' in response
      ? String((response as { id: unknown }).id)
      : null);
  if (id) return { to: `/emails/${id}`, label: 'View email' };
  const domain = path.match(/^\/domains\/(dom_\w+)/)?.[1];
  if (domain) return { to: `/domains/${domain}`, label: 'View domain' };
  return null;
}

export function LogDetail() {
  const { id = '' } = useParams();
  const {
    data: log,
    error,
    loading,
  } = useLoad(() => api<ApiRequestDetail>('GET', `/logs/${encodeURIComponent(id)}`), [id]);

  if (loading && !log) return <div className="skeleton" style={{ height: 28, width: 260 }} />;
  if (error || !log) return <ErrorNote error={error ?? 'Request not found.'} />;

  const request = pretty(log.requestBody);
  const response = pretty(log.responseBody);
  const related = relatedLink(log.path, log.responseBody);
  const errorMessage =
    log.statusCode >= 300 &&
    log.responseBody &&
    typeof log.responseBody === 'object' &&
    'error' in log.responseBody
      ? (log.responseBody as { error: { message?: string } }).error.message
      : null;

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/logs">Logs</Link>
            <span>/</span>
            <span className="mono">{log.id}</span>
          </>
        }
        title={
          <span className="log-title">
            <Method method={log.method} /> <span className="mono">{log.path}</span>
          </span>
        }
        description={
          <>
            <StatusCode code={log.statusCode} />{' '}
            <span style={{ marginLeft: 8 }}>
              {formatDateTime(log.createdAt)} · {log.durationMs} ms
            </span>
          </>
        }
        actions={
          related ? (
            <Link to={related.to} className="btn btn-ghost">
              {related.label}
            </Link>
          ) : null
        }
      />
      {errorMessage ? <div className="banner">{errorMessage}</div> : null}

      <div className="detail-grid">
        <div>
          <div className="card">
            <div className="card-head">
              <h2>Request body</h2>
            </div>
            <div className="card-body">
              {request ? <CodeBlock code={request} /> : <p className="muted">No body.</p>}
            </div>
          </div>
          <div className="card">
            <div className="card-head">
              <h2>Response body</h2>
            </div>
            <div className="card-body">
              {response ? <CodeBlock code={response} /> : <p className="muted">No body.</p>}
            </div>
          </div>
        </div>
        <div>
          <div className="card">
            <div className="card-head">
              <h2>Details</h2>
            </div>
            <div className="card-body">
              <dl className="facts">
                <dt>ID</dt>
                <dd>
                  <Copyable value={log.id} />
                </dd>
                <dt>API key</dt>
                <dd>{log.apiKeyName ?? 'Deleted key'}</dd>
                {log.query ? (
                  <>
                    <dt>Query</dt>
                    <dd className="mono">?{log.query}</dd>
                  </>
                ) : null}
                <dt>User agent</dt>
                <dd>{log.userAgent ?? '—'}</dd>
                <dt>IP address</dt>
                <dd className="mono">{log.ip ?? '—'}</dd>
              </dl>
              <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
                Attachment contents and secrets are never stored in logs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
